import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import {
  Package,
  Plus,
  RefreshCw,
  Layers,
  ChevronRight,
  Pencil,
  Trash2,
  Check,
  X,
} from 'lucide-react';
import { parseProjectJournalLogMessage } from '../../utils/stockLogLinks';

const InventoryStockPanel = ({
  selectedStockId,
  setSelectedStockId,
  setShowQuickStockModal,
  setShowExistingStockModal,
  stockItems,
  loading,
  fetchStock,
  currentStockFilters,
  stockSearch,
  setStockSearch,
  stockStatusFilter,
  setStockStatusFilter,
  stockClientFilter,
  setStockClientFilter,
  stockFilterItemId,
  setStockFilterItemId,
  statusOptions,
  clients,
  items,
  projects = [],
  groupedStockItems,
  expandedStockKey,
  handleToggleExpand,
  statusBorderMap,
  statusClassMap,
  getStatusLabel,
  getStockShareUrl,
  openQrModal,
  handleDeleteStock,
  openLogFor,
  setOpenLogFor,
  handleLoadLogs,
  fetchStockChildren,
  renderStockChildrenList,
  stockChildren,
  loadingChildren,
  expandedStockNodes,
  handleToggleStockNode,
  stockLogs,
  newLogMessage,
  setNewLogMessage,
  handleAddLog,
  handleUpdateLog,
  handleDeleteLog,
  handleInlineStockUpdate,
  linkableStockItems,
  loadingLinkable,
  fetchLinkableStockItems,
  handleLinkStockChild,
  handleUnlinkStockChild,
  handleCreateLinkedChild,
}) => {
  const hasFocus = Boolean(stockFilterItemId);
  const focusedItem = hasFocus
    ? items.find((item) => String(item.id) === String(stockFilterItemId))
    : null;
  const [inlineEdits, setInlineEdits] = useState({});
  const [savingStockId, setSavingStockId] = useState(null);
  const [qrPreview, setQrPreview] = useState('');
  const [qrPreviewLoading, setQrPreviewLoading] = useState(false);
  const [pendingLinkSlots, setPendingLinkSlots] = useState({});
  const [activeLinkSlot, setActiveLinkSlot] = useState(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [editingLog, setEditingLog] = useState(null);
  const [editingLogMessage, setEditingLogMessage] = useState('');
  const [editingLogDate, setEditingLogDate] = useState('');
  const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const searchTerm = stockSearch.trim().toLowerCase();
  const getItemLabel = (item) => {
    if (!item) return '';
    const base = item.item_name || '';
    return item.occurrence_name ? `${base} - ${item.occurrence_name}` : base;
  };
  const parseStockDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const raw = String(value).trim();
    if (!raw) return null;
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(raw);
    let normalized = raw.replace(' ', 'T');
    if (!hasTz) {
      normalized = `${normalized}Z`;
    }
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  const getTimeZoneParts = (date, timeZone) => {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = formatter.formatToParts(date);
    return parts.reduce((acc, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = part.value;
      }
      return acc;
    }, {});
  };
  const getTimeZoneOffset = (date, timeZone) => {
    const parts = getTimeZoneParts(date, timeZone);
    const utcTime = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second),
    );
    return (utcTime - date.getTime()) / 60000;
  };
  const formatParisDateTimeInput = (value) => {
    if (!value) return '';
    const date = parseStockDate(value);
    if (!date) return '';
    const parts = getTimeZoneParts(date, 'Europe/Paris');
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  };
  const formatParisDateTimeDisplay = (value) => {
    if (!value) return '-';
    const date = parseStockDate(value);
    if (!date) return value;
    return date.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' });
  };
  const normalizeParisDateTime = (value) => {
    if (!value) return null;
    const [datePart, timePart] = value.split('T');
    if (!datePart || !timePart) return null;
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    if ([year, month, day, hour, minute].some(Number.isNaN)) return null;
    const guess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
    const offset = getTimeZoneOffset(guess, 'Europe/Paris');
    const utcMillis = Date.UTC(year, month - 1, day, hour, minute, 0) - offset * 60000;
    return new Date(utcMillis).toISOString();
  };
  const renderLogMessage = (message) => {
    const { text, link } = parseProjectJournalLogMessage(message || '');
    if (!link) {
      return <div className="text-slate-700">{text}</div>;
    }
    const href = `/project/${link.projectId}?panel=journal&entryId=${link.entryId}`;
    return (
      <a
        href={href}
        className="text-slate-700 underline decoration-dotted hover:text-blue-600"
      >
        {text || 'Voir le journal'}
      </a>
    );
  };
  const startEditLog = (stockId, log) => {
    setEditingLog({ stockId, logId: log.id });
    setEditingLogMessage(log.message || '');
    setEditingLogDate(formatParisDateTimeInput(log.created_at));
  };
  const cancelEditLog = () => {
    setEditingLog(null);
    setEditingLogMessage('');
    setEditingLogDate('');
  };
  const submitEditLog = async (stockId, logId) => {
    if (!handleUpdateLog) return;
    const payload = { message: editingLogMessage };
    const normalizedDate = normalizeParisDateTime(editingLogDate);
    if (normalizedDate) {
      payload.created_at = normalizedDate;
    }
    const ok = await handleUpdateLog(stockId, logId, payload);
    if (ok) {
      cancelEditLog();
    }
  };
  const requestDeleteLog = async (stockId, logId) => {
    if (!handleDeleteLog) return;
    if (!window.confirm('Supprimer cette note ?')) return;
    const ok = await handleDeleteLog(stockId, logId);
    if (ok && editingLog?.logId === logId) {
      cancelEditLog();
    }
  };
  const renderLogEntry = (stockId, log) => {
    const isEditing =
      editingLog?.logId === log.id && editingLog?.stockId === stockId;
    return (
      <div key={log.id} className="group rounded-lg border bg-white px-3 py-2">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              rows={2}
              value={editingLogMessage}
              onChange={(e) => setEditingLogMessage(e.target.value)}
              className="w-full rounded border border-slate-200 px-2 py-1 text-xs text-slate-700"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <input
                type="datetime-local"
                value={editingLogDate}
                onChange={(e) => setEditingLogDate(e.target.value)}
                className="rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600"
              />
              <div className="flex items-center gap-2 text-[11px]">
                <button
                  type="button"
                  onClick={() => submitEditLog(stockId, log.id)}
                  className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-2.5 py-1 text-emerald-700 hover:bg-emerald-50"
                >
                  <Check className="h-3 w-3" />
                  Sauver
                </button>
                <button
                  type="button"
                  onClick={cancelEditLog}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-slate-600 hover:bg-slate-50"
                >
                  <X className="h-3 w-3" />
                  Annuler
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {renderLogMessage(log.message)}
            <div className="mt-1 text-[11px] text-slate-400">
              {log.created_by || '-'} - {formatParisDateTimeDisplay(log.created_at)}
            </div>
            <div className="mt-2 flex items-center gap-2 text-[11px] opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
              <button
                type="button"
                onClick={() => startEditLog(stockId, log)}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-slate-600 hover:bg-slate-50"
              >
                <Pencil className="h-3 w-3" />
                Modifier
              </button>
              <button
                type="button"
                onClick={() => requestDeleteLog(stockId, log.id)}
                className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-2.5 py-1 text-rose-600 hover:bg-rose-50"
              >
                <Trash2 className="h-3 w-3" />
                Supprimer
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (selectedStockId) {
      fetchStockChildren(selectedStockId);
    }
  }, [selectedStockId]);

  const collectVisibleNodeIds = (rootId) => {
    if (!rootId) return [];
    const ids = new Set();
    const stack = [rootId];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current || ids.has(current)) continue;
      ids.add(current);
      const children = stockChildren[current] || [];
      const isExpanded = expandedStockNodes[current] || current === rootId;
      if (isExpanded) {
        children.forEach((child) => {
          if (child?.id) stack.push(child.id);
        });
      }
    }
    return Array.from(ids);
  };

  const visibleNodeIds = useMemo(
    () => (selectedStockId ? collectVisibleNodeIds(selectedStockId) : []),
    [selectedStockId, stockChildren, expandedStockNodes],
  );

  useEffect(() => {
    visibleNodeIds.forEach((id) => {
      if (!hasOwn(stockLogs, id)) {
        handleLoadLogs(id);
      }
    });
  }, [visibleNodeIds, stockLogs, handleLoadLogs, hasOwn]);

  useEffect(() => {
    if (!searchTerm) return;
    const expandIds = new Set();
    groupedStockItems.forEach((item) => {
      (item.match_path_ids || []).forEach((id) => expandIds.add(id));
    });
    expandIds.forEach((id) => {
      if (!expandedStockNodes[id]) {
        handleToggleStockNode(id);
      }
    });
  }, [searchTerm, groupedStockItems, expandedStockNodes, handleToggleStockNode]);

  useEffect(() => {
    if (hasFocus && !selectedStockId && groupedStockItems.length > 0) {
      setSelectedStockId(groupedStockItems[0].id);
    }
  }, [hasFocus, selectedStockId, groupedStockItems, setSelectedStockId]);

  const findStockById = (id) => {
    if (!id) return null;
    const direct = stockItems.find((item) => item.id === id);
    if (direct) return direct;
    for (const group of groupedStockItems) {
      const match = (group.group_items || []).find((item) => item.id === id);
      if (match) return match;
    }
    const childBuckets = Object.values(stockChildren || {});
    for (const bucket of childBuckets) {
      const match = (bucket || []).find((item) => item.id === id);
      if (match) return match;
    }
    return null;
  };

  const selectedStockItem = useMemo(
    () => findStockById(selectedStockId),
    [selectedStockId, stockItems, groupedStockItems, stockChildren],
  );

  const matchesSearch = (item) => {
    if (!item || !searchTerm) return false;
    const haystack = [
      item.serial_number,
      item.item_name,
      item.occurrence_name,
      item.location,
      item.notes,
      item.tracking_number,
      item.client_name,
      item.project_name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(searchTerm);
  };

  const searchMatchIds = useMemo(() => {
    if (!searchTerm) return new Set();
    const ids = new Set();
    stockItems.forEach((item) => {
      (item.match_child_ids || []).forEach((id) => ids.add(id));
      if (matchesSearch(item)) {
        ids.add(item.id);
      }
    });
    return ids;
  }, [searchTerm, stockItems]);

  const findParentId = (childId) => {
    if (!childId) return null;
    for (const [parentId, children] of Object.entries(stockChildren || {})) {
      if ((children || []).some((child) => child?.id === childId)) {
        return Number(parentId);
      }
    }
    return null;
  };

  const selectedParentItem = useMemo(() => {
    if (!selectedStockId) return null;
    const parentId = findParentId(selectedStockId);
    return parentId ? findStockById(parentId) : null;
  }, [selectedStockId, stockChildren, stockItems, groupedStockItems]);

  useEffect(() => {
    let active = true;
    if (!selectedStockItem) {
      setQrPreview('');
      return undefined;
    }
    setQrPreviewLoading(true);
    const url = getStockShareUrl
      ? getStockShareUrl(selectedStockItem)
      : `${window.location.origin}/inventory?stock=${selectedStockItem.id}`;
    QRCode.toDataURL(url, { margin: 1, width: 160 })
      .then((dataUrl) => {
        if (active) {
          setQrPreview(dataUrl);
        }
      })
      .catch(() => {
        if (active) {
          setQrPreview('');
        }
      })
      .finally(() => {
        if (active) {
          setQrPreviewLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [selectedStockItem, getStockShareUrl]);

  const updateInlineEdit = (stockId, key, value) => {
    setInlineEdits((prev) => ({
      ...prev,
      [stockId]: {
        ...prev[stockId],
        [key]: value,
      },
    }));
  };

  const getInlineValue = (item, key) => {
    if (!item) return '';
    return inlineEdits[item.id]?.[key] ?? item[key] ?? '';
  };

  const handleSaveInline = async (item) => {
    if (!item) return;
    const edits = inlineEdits[item.id];
    if (!edits) return;
    setSavingStockId(item.id);
    await handleInlineStockUpdate(item.id, edits);
    setSavingStockId((prev) => (prev === item.id ? null : prev));
    setInlineEdits((prev) => {
      const next = { ...prev };
      delete next[item.id];
      return next;
    });
  };

  const persistInline = async (item) => {
    if (!item) return;
    const edits = inlineEdits[item.id];
    if (!edits) return;
    const nextOccurrenceName = getInlineValue(item, 'occurrence_name');
    const nextStatus = getInlineValue(item, 'status');
    const nextLocation = getInlineValue(item, 'location');
    const nextNotes = getInlineValue(item, 'notes');
    const nextProjectId = getInlineValue(item, 'project_id');
    const hasChanges =
      (nextOccurrenceName || '') !== (item.occurrence_name || '') ||
      nextStatus !== item.status ||
      (nextLocation || '') !== (item.location || '') ||
      (nextNotes || '') !== (item.notes || '') ||
      String(nextProjectId || '') !== String(item.project_id || '');
    if (!hasChanges) {
      setInlineEdits((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      return;
    }
    await handleSaveInline({
      ...item,
      occurrence_name: nextOccurrenceName,
      status: nextStatus,
      location: nextLocation,
      notes: nextNotes,
      project_id: nextProjectId,
    });
  };

  const renderInlineFields = (item) => (
    <div className="mt-2 grid grid-cols-1 gap-1 md:grid-cols-5 text-xs">
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Nom</span>
        <input
          type="text"
          value={getInlineValue(item, 'occurrence_name')}
          onChange={(e) => updateInlineEdit(item.id, 'occurrence_name', e.target.value)}
          onBlur={() => persistInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
          placeholder="Optionnel"
        />
      </label>
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Statut</span>
        <select
          value={getInlineValue(item, 'status')}
          onChange={(e) => updateInlineEdit(item.id, 'status', e.target.value)}
          onBlur={() => persistInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Emplacement</span>
        <input
          type="text"
          value={getInlineValue(item, 'location')}
          onChange={(e) => updateInlineEdit(item.id, 'location', e.target.value)}
          onBlur={() => persistInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
        />
      </label>
      <label className="space-y-1">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Projet</span>
        <select
          value={getInlineValue(item, 'project_id') ?? ''}
          onChange={(e) => updateInlineEdit(item.id, 'project_id', e.target.value)}
          onBlur={() => persistInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
        >
          <option value="">Aucun projet</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </label>
      <label className="space-y-1 md:col-span-5">
        <span className="text-[10px] uppercase tracking-wide text-slate-400">Notes</span>
        <textarea
          rows={1}
          value={getInlineValue(item, 'notes')}
          onChange={(e) => updateInlineEdit(item.id, 'notes', e.target.value)}
          onBlur={() => persistInline(item)}
          className="w-full rounded border border-slate-200 px-2 py-1 text-xs"
        />
      </label>
      {savingStockId === item.id && (
        <div className="text-[11px] text-slate-400 md:col-span-3">
          Sauvegarde...
        </div>
      )}
    </div>
  );

  const addPendingSlot = (parentId, child) => {
    if (!parentId || !child?.item_id) return;
    const slotId = `${parentId}-${child.id}-${Date.now()}`;
    const slot = {
      id: slotId,
      item_id: child.item_id,
      item_version_id: child.item_version_id,
      item_name: child.item_name,
    };
    setPendingLinkSlots((prev) => ({
      ...prev,
      [parentId]: [...(prev[parentId] || []), slot],
    }));
    setActiveLinkSlot(slotId);
  };

  const removePendingSlot = (parentId, slotId) => {
    setPendingLinkSlots((prev) => {
      const next = { ...prev };
      const slots = next[parentId] || [];
      next[parentId] = slots.filter((slot) => slot.id !== slotId);
      if (next[parentId].length === 0) {
        delete next[parentId];
      }
      return next;
    });
    if (activeLinkSlot === slotId) {
      setActiveLinkSlot(null);
      setLinkSearch('');
    }
  };

  const renderFocusedNode = (item, depth = 0, parent = null) => {
    if (!item) return null;
    const children = stockChildren[item.id] || [];
    const isExpanded = expandedStockNodes[item.id] || depth === 0;
    const logs = stockLogs[item.id] || [];
    const pendingSlots = pendingLinkSlots[item.id] || [];
    const isMatch = searchMatchIds.has(item.id);
    const isChild = depth > 0;
    const versionDisplay = item.version_number
      ? `V${item.version_number}${item.version_label ? ` ${item.version_label}` : ''}`
      : item.version_label || '';
    return (
      <div
        className={`rounded-2xl border p-3 ${
          isChild
            ? 'mt-3 ml-3 border-l-4 border-l-slate-300/80 border-slate-300/80 bg-slate-50/90'
            : 'border-slate-200/80 bg-white'
        } ${isMatch ? 'ring-2 ring-amber-300' : ''}`}
      >
        {depth === 0 ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">
                  Occurrence
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <div className="text-sm font-semibold text-slate-900">
                    {item.serial_number || `#${item.id}`} - {getItemLabel(item)}
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                      statusClassMap[item.status] || 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {getStatusLabel(item.status)}
                  </span>
                </div>
                {parent && (
                  <div className="mt-1 text-[11px] text-slate-500">
                    Enfant de{' '}
                    <button
                      type="button"
                      onClick={() => setSelectedStockId(parent.id)}
                      className="font-semibold text-slate-700 hover:text-slate-900"
                    >
                      {parent.serial_number || `#${parent.id}`} - {getItemLabel(parent)}
                    </button>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-slate-500">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">
                      Version
                    </div>
                    <div className="text-slate-700">{versionDisplay || '-'}</div>
                  </div>
                  {item.version_description && (
                    <div className="max-w-xl">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">
                        Notes version
                      </div>
                      <div className="text-slate-700">{item.version_description}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-3 rounded-xl border border-slate-300/70 bg-white px-3 py-2 shadow-sm">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">
                    QR code
                  </div>
                  <div className="h-16 w-16 rounded bg-white p-1 shadow-sm">
                    {qrPreviewLoading ? (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">
                        ...
                      </div>
                    ) : qrPreview ? (
                      <img src={qrPreview} alt="QR code" className="h-full w-full" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">
                        -
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openQrModal(item)}
                    className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600 hover:bg-white"
                  >
                    Agrandir
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteStock(item)}
                  className="rounded-full border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                >
                  Supprimer
                </button>
              </div>
            </div>
            <div className="mt-3 h-px bg-slate-200/80" />
          </>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <button
              type="button"
              onClick={() => {
                if (item.child_count) {
                  handleToggleStockNode(item.id);
                }
              }}
              className="text-left"
            >
              <div className="mb-1 inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                <Layers className="h-3 w-3" />
                Sous-item
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {item.serial_number || `#${item.id}`} - {getItemLabel(item)}
              </div>
              <div className="text-[11px] text-slate-500">
                {item.client_name ? `Proprietaire: ${item.client_name}` : ''}
                {item.location ? ` - ${item.location}` : ''}
              </div>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  statusClassMap[item.status] || 'bg-gray-100 text-gray-600'
                }`}
              >
                {getStatusLabel(item.status)}
              </span>
              {item.child_count ? (
                <button
                  type="button"
                  onClick={() => {
                    handleToggleStockNode(item.id);
                    handleLoadLogs(item.id);
                  }}
                  className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
                >
                  {isExpanded ? 'Masquer' : `Sous-items (${item.child_count})`}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setSelectedStockId(item.id)}
                className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
              >
                Focus
              </button>
              {parent?.id && (
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await handleUnlinkStockChild(parent.id, item.id);
                    if (ok) {
                      addPendingSlot(parent.id, item);
                    }
                  }}
                  className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-500 hover:bg-slate-50"
                >
                  Delier
                </button>
              )}
            </div>
          </div>
        )}
        <div className="mt-2 grid gap-2 md:grid-cols-[2fr_1fr]">
          <div>
            {renderInlineFields(item)}
            {isExpanded && (
              <div className="mt-3 rounded-xl border border-dashed border-slate-300/70 bg-slate-50/90 p-3">
                <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-wide text-slate-400">
                  <span className="inline-flex items-center gap-1">
                    <Layers className="h-3 w-3" />
                    Sous-items
                  </span>
                  {item.child_count ? (
                    <span className="text-[11px] text-slate-400">
                      {item.child_count}
                    </span>
                  ) : null}
                </div>
                <div className="space-y-2">
                  {loadingChildren[item.id] && (
                    <div className="text-[11px] text-slate-400">Chargement...</div>
                  )}
                  {!loadingChildren[item.id] && children.length === 0 && (
                    <div className="text-[11px] text-slate-400">
                      Aucun sous-item.
                    </div>
                  )}
                  {children.map((child) => renderFocusedNode(child, depth + 1, item))}
                  {pendingSlots.map((slot) => {
                    const isActive = activeLinkSlot === slot.id;
                    const available = linkableStockItems
                      .filter((stockItem) => stockItem.item_id === slot.item_id)
                      .filter((stockItem) => {
                        if (!linkSearch.trim()) return true;
                        const term = linkSearch.toLowerCase();
                        const serial = (stockItem.serial_number || '').toLowerCase();
                        const name = (stockItem.item_name || '').toLowerCase();
                        const occurrence = (stockItem.occurrence_name || '').toLowerCase();
                        return (
                          serial.includes(term) ||
                          name.includes(term) ||
                          occurrence.includes(term)
                        );
                      });
                    return (
                      <div
                        key={slot.id}
                        className="rounded-xl border border-dashed border-slate-200 bg-white p-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-xs font-semibold text-slate-700">
                            Sous-item manquant: {slot.item_name || `#${slot.item_id}`}
                          </div>
                          <div className="flex items-center gap-2 text-[11px]">
                            <button
                              type="button"
                              onClick={() => {
                                setActiveLinkSlot(slot.id);
                                setLinkSearch('');
                                fetchLinkableStockItems();
                              }}
                              className="rounded-full border border-slate-200 px-2.5 py-1 text-slate-600 hover:bg-white"
                            >
                              Lier existant
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const ok = await handleCreateLinkedChild(item, slot);
                                if (ok) {
                                  removePendingSlot(item.id, slot.id);
                                }
                              }}
                              className="rounded-full border border-slate-200 px-2.5 py-1 text-slate-600 hover:bg-white"
                            >
                              Creer
                            </button>
                            <button
                              type="button"
                              onClick={() => removePendingSlot(item.id, slot.id)}
                              className="rounded-full border border-transparent px-2.5 py-1 text-slate-400 hover:text-slate-600"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                        {isActive && (
                          <div className="mt-2 space-y-2">
                            <input
                              type="text"
                              placeholder="Rechercher un item existant"
                              value={linkSearch}
                              onChange={(event) => setLinkSearch(event.target.value)}
                              className="w-full rounded border border-slate-200 px-2 py-1 text-[11px]"
                            />
                            {loadingLinkable ? (
                              <div className="text-[11px] text-slate-400">
                                Chargement...
                              </div>
                            ) : available.length === 0 ? (
                              <div className="text-[11px] text-slate-400">
                                Aucun item disponible.
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {available.slice(0, 6).map((stockItem) => (
                                  <button
                                    key={stockItem.id}
                                    type="button"
                                    onClick={async () => {
                                      const ok = await handleLinkStockChild(
                                        item.id,
                                        stockItem.id,
                                      );
                                      if (ok) {
                                        removePendingSlot(item.id, slot.id);
                                      }
                                    }}
                                    className="flex w-full items-center justify-between rounded border border-slate-200 bg-white px-2 py-1 text-left text-[11px] text-slate-700 hover:bg-slate-50"
                                  >
                                    <span>
                                      {stockItem.serial_number || `#${stockItem.id}`} -{' '}
                                      {getItemLabel(stockItem)}
                                    </span>
                                    <span className="text-[10px] text-slate-400">
                                      {getStatusLabel(stockItem.status)}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="rounded-xl border border-slate-200/70 bg-slate-50/70 p-3 text-[11px] text-slate-500 min-h-[180px]">
            <div className="flex items-center justify-between">
              <span className="uppercase tracking-wide text-[10px] text-slate-400">
                Journal
              </span>
            </div>
            <div className="mt-2 max-h-48 space-y-1 overflow-auto">
              {logs.map((log) => renderLogEntry(item.id, log))}
              {logs.length === 0 && (
                <div className="text-slate-400">Aucune note</div>
              )}
            </div>
            <div className="mt-2 flex gap-1">
              <input
                type="text"
                placeholder="Ajouter une note"
                value={newLogMessage[item.id] || ''}
                onChange={(e) =>
                  setNewLogMessage((prev) => ({
                    ...prev,
                    [item.id]: e.target.value,
                  }))
                }
                className="flex-1 rounded border border-slate-200 px-2 py-1 text-[11px]"
              />
              <button
                type="button"
                onClick={() => handleAddLog(item.id)}
                className="rounded bg-slate-900 px-2 py-1 text-[10px] text-white"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
    <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-white via-white to-slate-50 p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold uppercase text-gray-500">Ajouter au stock</h2>
          <p className="text-xs text-gray-500">
            Choisissez un mode d ajout selon le besoin.
          </p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <button
          type="button"
          onClick={() => setShowQuickStockModal(true)}
          className="rounded-2xl border border-slate-200/80 bg-white p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Plus className="h-4 w-4" />
            Ajout rapide
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Achat simple : cree item + occurrence.
          </p>
        </button>
        <button
          type="button"
          onClick={() => setShowExistingStockModal(true)}
          className="rounded-2xl border border-slate-200/80 bg-white p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Layers className="h-4 w-4" />
            Occurrence existante
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Selectionnez l item et la version deja definie.
          </p>
        </button>
      </div>
    </div>

    <div className="rounded-2xl border border-slate-200/70 bg-white/90 p-6 shadow-sm space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Stocks actuels</h2>
          <p className="text-xs text-gray-500">
            Occurrences en cours, en stock ou chez le proprietaire.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            {stockItems.length} occurrences
          </span>
          <button
            type="button"
            onClick={() => fetchStock(currentStockFilters())}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
            disabled={loading.stock}
          >
            <RefreshCw className={`h-4 w-4 ${loading.stock ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>
      {selectedStockId && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-300/70 bg-slate-50/80 px-4 py-3">
          <button
            type="button"
            onClick={() => setSelectedStockId(null)}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800"
          >
            Retour a la liste
          </button>
          <div className="text-xs font-semibold text-slate-600">
            Vue detaillee
            {selectedStockItem ? (
              <span className="ml-2 text-slate-500">
                {selectedStockItem.serial_number || `#${selectedStockItem.id}`} -{' '}
                {getItemLabel(selectedStockItem)}
              </span>
            ) : null}
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
          <input
            type="text"
            placeholder="Recherche (numero, item)"
            value={stockSearch}
            onChange={(e) => setStockSearch(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          />
          <select
            value={stockStatusFilter}
            onChange={(e) => setStockStatusFilter(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Tous les statuts</option>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={stockClientFilter}
            onChange={(e) => setStockClientFilter(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Tous les proprietaires</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
          <select
            value={stockFilterItemId}
            onChange={(e) => setStockFilterItemId(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Tous les items</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {hasFocus && (
        <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400">
                Item selectionne
              </div>
              <div className="mt-1 text-xl font-semibold text-slate-900">
                {focusedItem?.name || 'Item'}
              </div>
              {focusedItem?.description && (
                <div className="mt-1 text-sm text-slate-500">
                  {focusedItem.description}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className="rounded-full bg-slate-100 px-3 py-1">
                {stockItems.length} occurrences
              </span>
              {groupedStockItems.length > 0 && (
                <span className="rounded-full bg-slate-100 px-3 py-1">
                  {groupedStockItems.length} groupes
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {loading.stock ? (
        <div className="text-sm text-slate-500">Chargement...</div>
      ) : selectedStockId ? (
        <div className="space-y-4">
          {!selectedStockItem && (
            <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-4 text-sm text-slate-500">
              Occurrence introuvable.
            </div>
          )}
          {selectedStockItem && (
            <>
              {renderFocusedNode(selectedStockItem, 0, selectedParentItem)}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {stockItems.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
              Aucune occurrence enregistree.
            </div>
          )}
          {groupedStockItems.map((stockItem) => {
            const isSearchExpanded =
              Boolean(searchTerm) &&
              Boolean(stockItem.match_child_ids?.length || stockItem.match_path_ids?.length);
            const isExpanded = hasFocus
              ? true
              : isSearchExpanded || expandedStockKey === stockItem.group_key;
            const borderClass = statusBorderMap[stockItem.status] || 'border-slate-200/80';
            const isGroupMatch =
              Boolean(searchTerm) &&
              (searchMatchIds.has(stockItem.id) || stockItem.match_child_ids?.length);
            return (
              <div
                key={stockItem.group_key}
                className={`rounded-2xl border ${borderClass} bg-white/95 p-4 shadow-sm transition transform hover:-translate-y-0.5 hover:shadow-lg ${
                  isExpanded ? 'ring-1 ring-slate-200/60' : ''
                } ${isGroupMatch ? 'ring-2 ring-amber-300' : ''}`}
              >
                <div
                  className={`flex flex-wrap items-start justify-between gap-4 ${
                    hasFocus ? '' : 'cursor-pointer'
                  }`}
                  onClick={() => {
                    if (hasFocus) {
                      if (stockItem.group_count === 1) {
                        setSelectedStockId(stockItem.id);
                      }
                      return;
                    }
                    if (stockItem.group_count === 1) {
                      setSelectedStockId(stockItem.id);
                      return;
                    }
                    handleToggleExpand(
                      stockItem.group_key,
                      stockItem.id,
                      stockItem.group_count,
                    );
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                      <Package className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {stockItem.group_count > 1
                          ? `${getItemLabel(stockItem)} x${stockItem.group_count}`
                          : `${stockItem.serial_number || `#${stockItem.id}`} - ${
                              getItemLabel(stockItem)
                            }`}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                        {stockItem.version_number && (
                          <span className="rounded-full bg-slate-100 px-2 py-1">
                            V{stockItem.version_number}
                            {stockItem.version_label ? ` ${stockItem.version_label}` : ''}
                          </span>
                        )}
                        {stockItem.location && (
                          <span className="rounded-full bg-slate-100 px-2 py-1">
                            Emplacement: {stockItem.location}
                          </span>
                        )}
                        {stockItem.client_name && (
                          <span className="rounded-full bg-slate-100 px-2 py-1">
                            Proprietaire: {stockItem.client_name}
                          </span>
                        )}
                        {stockItem.project_name && (
                          <span className="rounded-full bg-slate-100 px-2 py-1">
                            Projet: {stockItem.project_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                        statusClassMap[stockItem.status] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {getStatusLabel(stockItem.status)}
                    </span>
                    {stockItem.group_count > 1 ? (
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] text-slate-500">
                        {stockItem.group_count} occurrences
                      </span>
                    ) : stockItem.child_count ? (
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] text-slate-500">
                        {stockItem.child_count} sous-items
                      </span>
                    ) : null}
                    {!hasFocus && (
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition ${
                          isExpanded ? 'bg-slate-900 text-white border-slate-900' : 'bg-white'
                        }`}
                      >
                        <ChevronRight
                          className={`h-4 w-4 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                        />
                      </span>
                    )}
                  </div>
                </div>

                {isExpanded && stockItem.group_count === 1 && (
                  <div className="mt-4 space-y-4">
                    <div className="rounded-2xl border border-slate-200/70 bg-slate-50/70 p-4 text-xs">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Proprietaire
                          </div>
                          <div className="text-slate-700">
                            {stockItem.client_name || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Projet
                          </div>
                          <div className="text-slate-700">
                            {stockItem.project_name || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Emplacement
                          </div>
                          <div className="text-slate-700">
                            {stockItem.location || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Suivi
                          </div>
                          <div className="text-slate-700">
                            {stockItem.tracking_number || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Version
                          </div>
                          <div className="text-slate-700">
                            {stockItem.version_number
                              ? `V${stockItem.version_number}`
                              : '-'}
                            {stockItem.version_label ? ` ${stockItem.version_label}` : ''}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Etat
                          </div>
                          <div className="text-slate-700">
                            {getStatusLabel(stockItem.status)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Commande
                          </div>
                          <div className="text-slate-700">
                            {stockItem.ordered_at
                              ? new Date(stockItem.ordered_at).toLocaleString()
                              : '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Reception
                          </div>
                          <div className="text-slate-700">
                            {stockItem.received_at
                              ? new Date(stockItem.received_at).toLocaleString()
                              : '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Envoi
                          </div>
                          <div className="text-slate-700">
                            {stockItem.shipped_at
                              ? new Date(stockItem.shipped_at).toLocaleString()
                              : '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Suppression
                          </div>
                          <div className="text-slate-700">
                            {stockItem.removed_at
                              ? new Date(stockItem.removed_at).toLocaleString()
                              : '-'}
                          </div>
                        </div>
                        <div className="md:col-span-2 lg:col-span-3">
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Notes
                          </div>
                          <div className="text-slate-700">
                            {stockItem.notes || '-'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-300/70 bg-slate-50/90 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500">
                          <Layers className="h-3 w-3" />
                          Sous-items
                        </div>
                        {stockItem.child_count ? (
                          <span className="text-[11px] text-slate-400">
                            {stockItem.child_count}
                          </span>
                        ) : null}
                      </div>
                      {hasFocus && stockItem.child_count ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStockNode(stockItem.id);
                          }}
                          className="mt-3 inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-white"
                        >
                          {expandedStockNodes[stockItem.id]
                            ? 'Masquer les sous-items'
                            : `Voir les sous-items (${stockItem.child_count})`}
                        </button>
                      ) : null}
                      {hasFocus ? (
                        expandedStockNodes[stockItem.id] && (
                          <div className="mt-3 rounded-xl border border-dashed border-slate-300/70 bg-white p-3">
                            {renderStockChildrenList(
                              stockItem.id,
                              0,
                              searchMatchIds,
                            )}
                          </div>
                        )
                      ) : (
                        <div className="mt-3 rounded-xl border border-dashed border-slate-300/70 bg-white p-3">
                          {renderStockChildrenList(stockItem.id, 0, searchMatchIds)}
                        </div>
                      )}
                    </div>

                    {(hasFocus || openLogFor === stockItem.id) && (
                      <div className="rounded-2xl border border-slate-200/70 bg-white p-4 text-xs space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Journal
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = openLogFor === stockItem.id ? null : stockItem.id;
                              setOpenLogFor(next);
                              if (next) {
                                handleLoadLogs(stockItem.id);
                              }
                            }}
                            className="rounded-full border border-slate-200 px-3 py-1 text-[11px] text-slate-600 hover:bg-slate-50"
                          >
                            {openLogFor === stockItem.id ? 'Masquer' : 'Afficher'}
                          </button>
                        </div>
                        {openLogFor !== stockItem.id && (
                          <div className="text-[11px] text-slate-400">
                            Cliquez pour afficher le journal.
                          </div>
                        )}
                        {openLogFor === stockItem.id && (
                          <>
                            <div className="space-y-1 max-h-40 overflow-auto">
                              {(stockLogs[stockItem.id] || []).map((log) =>
                                renderLogEntry(stockItem.id, log),
                              )}
                              {(stockLogs[stockItem.id] || []).length === 0 && (
                                <div className="text-slate-400">Aucune note</div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Ajouter une note"
                                value={newLogMessage[stockItem.id] || ''}
                                onChange={(e) =>
                                  setNewLogMessage((prev) => ({
                                    ...prev,
                                    [stockItem.id]: e.target.value,
                                  }))
                                }
                                className="flex-1 rounded-xl border border-slate-200 px-2 py-1"
                              />
                              <button
                                type="button"
                                onClick={() => handleAddLog(stockItem.id)}
                                className="rounded-xl bg-blue-600 px-3 py-1 text-white"
                              >
                                Ajouter
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {isExpanded && stockItem.group_count > 1 && (
                  <div className="mt-4 space-y-4">
                    <div className="rounded-2xl border border-slate-200/70 bg-slate-50/70 p-4 text-xs">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Quantite
                          </div>
                          <div className="text-slate-700">{stockItem.group_count}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Proprietaire
                          </div>
                          <div className="text-slate-700">
                            {stockItem.client_name || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Projet
                          </div>
                          <div className="text-slate-700">
                            {stockItem.project_name || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Emplacement
                          </div>
                          <div className="text-slate-700">
                            {stockItem.location || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Suivi
                          </div>
                          <div className="text-slate-700">
                            {stockItem.tracking_number || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">
                            Version
                          </div>
                          <div className="text-slate-700">
                            {stockItem.version_number
                              ? `V${stockItem.version_number}`
                              : '-'}
                            {stockItem.version_label ? ` ${stockItem.version_label}` : ''}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200/70 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400">
                          Occurrences
                        </div>
                        {stockItem.group_child_total ? (
                          <span className="text-[11px] text-slate-400">
                            {stockItem.group_child_total} sous-items total
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-3 space-y-2">
                        {(stockItem.group_items || []).map((item) => {
                          const isChildExpanded = Boolean(expandedStockNodes[item.id]);
                          const isLogOpen = openLogFor === item.id;
                          const isMatch = searchMatchIds.has(item.id) || matchesSearch(item);
                          return (
                            <div
                              key={item.id}
                              className={`rounded-xl border border-slate-200/80 bg-slate-50/60 px-3 py-2 cursor-pointer ${
                                isMatch ? 'ring-2 ring-amber-300' : ''
                              }`}
                              onClick={() => setSelectedStockId(item.id)}
                            >
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                  <div className="text-xs font-semibold text-slate-800">
                                    {item.serial_number || `#${item.id}`} - {getItemLabel(item)}
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    {item.location ? `Emplacement: ${item.location}` : '-'}
                                    {item.created_at
                                      ? ` | Ajoute: ${new Date(
                                          item.created_at,
                                        ).toLocaleString()}`
                                      : ''}
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                  <span
                                    className={`rounded-full px-2 py-1 font-semibold ${
                                      statusClassMap[item.status] ||
                                      'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {getStatusLabel(item.status)}
                                  </span>
                                  {item.child_count ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleStockNode(item.id);
                                      }}
                                      className="rounded-full border border-transparent px-2 py-1 text-slate-600 transition hover:border-slate-200 hover:bg-slate-50"
                                    >
                                      {isChildExpanded
                                        ? 'Masquer'
                                        : `Sous-items (${item.child_count})`}
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const next = isLogOpen ? null : item.id;
                                      setOpenLogFor(next);
                                      if (next) {
                                        handleLoadLogs(item.id);
                                      }
                                    }}
                                    className="rounded-full border border-transparent px-2 py-1 text-gray-600 transition hover:border-gray-200 hover:bg-gray-50"
                                  >
                                    Journal
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openQrModal(item);
                                    }}
                                    className="rounded-full border border-transparent px-2 py-1 text-slate-600 transition hover:border-slate-200 hover:bg-slate-50"
                                  >
                                    QR
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteStock(item);
                                    }}
                                    className="rounded-full border border-transparent px-2 py-1 text-rose-600 transition hover:border-rose-200 hover:bg-rose-50"
                                  >
                                    Supprimer
                                  </button>
                                </div>
                              </div>

                              {isChildExpanded && (
                                <div className="mt-3 rounded-xl border border-dashed border-slate-300/70 bg-slate-50/90 p-3">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500">
                                      <Layers className="h-3 w-3" />
                                      Sous-items
                                    </div>
                                  </div>
                                  {renderStockChildrenList(item.id, 1, searchMatchIds)}
                                </div>
                              )}

                              {isLogOpen && (
                                <div className="mt-3 rounded-xl border border-slate-200/70 bg-white p-3 text-xs space-y-2">
                                  <div className="text-[11px] text-slate-400">
                                    Journal: {item.serial_number || `#${item.id}`}
                                  </div>
                                  <div className="space-y-1 max-h-40 overflow-auto">
                                    {(stockLogs[item.id] || []).map((log) =>
                                      renderLogEntry(item.id, log),
                                    )}
                                    {(stockLogs[item.id] || []).length === 0 && (
                                      <div className="text-slate-400">Aucune note</div>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      placeholder="Ajouter une note"
                                      value={newLogMessage[item.id] || ''}
                                      onChange={(e) =>
                                        setNewLogMessage((prev) => ({
                                          ...prev,
                                          [item.id]: e.target.value,
                                        }))
                                      }
                                      className="flex-1 rounded-xl border border-slate-200 px-2 py-1"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleAddLog(item.id)}
                                      className="rounded-xl bg-blue-600 px-3 py-1 text-white"
                                    >
                                      Ajouter
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
  );
};

export default InventoryStockPanel;
